import React, {useEffect, useState, useRef} from 'react';
import axios from 'axios';

import {
	useApplicationState, updateApplicationTitle,
} from '../../utilities/applicationState';
import MembersCard from './membersCard';
import MembersAdd from './modals/membersAdd';
import MembersEdit from './modals/membersEdit';
import {RolesList} from '../common';
import './members.scss';
import {SdfAlert} from '@synerg/react-components';
import {MemberRolesList} from 'shared-one-ux-components';
import _ from 'lodash';
import {withSession} from '../session';
import Toast from '../common/toast';
import ResetSuspendActivateWrapper from './modals/resetSuspendActivateWrapper';
import {SdfBusyIndicator} from '@synerg/react-components';
import orderBy from 'lodash/orderBy';

const MODALS = {
	ADD: 'ADD',
	DETAILS: 'DETAILS',
	EDIT: 'EDIT',
	PASSWORD_RESET: 'PASSWORD_RESET',
	SUSPEND: 'SUSPEND',
	ACTIVATE: 'ACTIVATE',
};

function MemberManagement({
	ooid,
	adminView = false,
	partnerType = 'marketplace',
	roles = [],
	session = {},
	orgName,
}) {
	const [appState, dispatch] = useApplicationState();
	const [membersList, setMembersList] = useState([]);
	const [loading, setLoading] = useState(false);

	const [activeMembersList, setActiveMembersList] = useState([]);
	const [suspendedMembersList, setSuspendedMembersList] = useState([]);
	const [errorLoadingMembers, setErrorLoadingMembers] = useState(false);
	const [selectedMember, setSelectedMember] = useState({});
	const [memberRoles, setMemberRoles] = useState([]);
	const [, setRolesIncluded] = useState({});
	const [allRoles, setAllRoles] = useState([]);

	const [openAddModal, setOpenAddModal] = useState(false);
	const [, setOpenDetailsModal] = useState(false);
	const [openEditModal, setOpenEditModal] = useState(false);
	const [openPasswordResetModal, setOpenPasswordResetModal] = useState(false);
	const [openSuspendModal, setOpenSuspendModal] = useState(false);
	const [openActivateModal, setOpenActivateModal] = useState(false);
	const [modeState, setModeState] = useState('');
	const [size] = useState({
		x: window.innerWidth || 2000, y: window.innerHeight || 0,
	});
	const roleDescriptions = [
		{
			title: 'Project user',
			description: 'Able to access project, members, and certificate tabs.',
			type: 'marketplace',
			roleCode: 'selfservicepartneruser',
		}, {
			title: 'Certificate user',
			description: 'Able to access the certificate tab.',
			type: 'marketplace',
			roleCode: 'selfservicecertificatemanager',
		}, {
			title: 'Revenue share user',
			description: 'Able access the rev share tab.',
			type: 'marketplace',
			roleCode: 'rsPartner',
		}, {
			title: 'Financial user',
			description: 'Can provision and manage Billing & Invoice (iReceivables) access for the partner.',
			type: 'marketplace',
			roleCode: 'selfservicefinancialuser',
		}, {
			title: 'DataCloud user',
			description: 'Able to access project, members, usage, and certificate tabs.',
			type: 'datacloud',
			roleCode: 'selfservicedataclouduser',
		}, {
			title: 'Certificate user',
			description: 'Able to access the certificate tab.',
			type: 'datacloud',
			roleCode: 'selfservicecertificatemanager',
		}];

	const [toast, setToastData] = useState([false, {status: '', message: ''}]);
	const [toastCount, setToastCount] = useState(0);
	const [loggedInAoid, setAoid] = useState('');
	const [refreshMembers, setRefreshMembers] = useState(false);
	const [partnerTypeFromRoles] = useState(
			(roles.includes('selfservicedataclouduser')
					? 'datacloud'
					: 'marketplace'));

	const fetchMembers = async() => {
		try {
			setErrorLoadingMembers(false);
			setLoading(true);
			const {data: {members = [], rolesIncluded}} = await axios.get(
					`${import.meta.env.VITE_ROOT_PATH}/api/members${adminView
							? '?ooid='.concat(ooid)
							: ''}`);

			const active = [];
			const suspended = [];
			const list = [];

			members.forEach((member) => {
				if (member.status === 'Active') {
					active.push(member);
					list.push(member);
				} else {
					suspended.push(member);
					list.push({
						...member, isSuspended: true,
					});
				}
			});

			setMembersList(
					orderBy(list, ['status', member => member.firstName.toLowerCase()],
							['asc', 'asc']));

			setActiveMembersList(active);
			setSuspendedMembersList(suspended);
			setRolesIncluded(rolesIncluded);
			setLoading(false);
		} catch (error) {
			setActiveMembersList([]);
			setErrorLoadingMembers(true);
			setLoading(false);
		}
	};

	const fetchRoles = async() => {
		try {
			let typeOfPartner = adminView ? partnerType : (roles.includes(
					'selfservicedataclouduser') ? 'datacloud' : 'marketplace');
			
			// Convert B2C to marketplace
			if (typeOfPartner === 'B2C') {
				typeOfPartner = 'marketplace';
			}
			
			const {data} = await axios.get(
					`${import.meta.env.VITE_ROOT_PATH}/api/member/roles/${typeOfPartner}`);
			setMemberRoles(data);
		} catch (error) {
			setMemberRoles([]);
		}
	};

	const fetchAllRoles = async() => {
		try {
			let typeOfPartner = adminView ? partnerType : (roles.includes(
					'selfservicedataclouduser') ? 'datacloud' : 'marketplace');
			
			// Convert B2C to marketplace
			if (typeOfPartner === 'B2C') {
				typeOfPartner = 'marketplace';
			}
			
			const {data} = await axios.get(
					`${import.meta.env.VITE_ROOT_PATH}/api/member/roles/all/${typeOfPartner}`);
			setAllRoles(data);
		} catch (error) {
			setAllRoles([]);
		}
	};

	const switchToModal = (modal, data) => {
		setOpenAddModal(modal === MODALS.ADD);
		setOpenDetailsModal(modal === MODALS.DETAILS);
		setOpenEditModal(modal === MODALS.EDIT);
		setOpenPasswordResetModal(modal === MODALS.PASSWORD_RESET);
		setOpenSuspendModal(modal === MODALS.SUSPEND);
		setOpenActivateModal(modal === MODALS.ACTIVATE);

		if (data) {
			setSelectedMember(data);
		}
	};

	const closeModals = () => switchToModal();

	const onOpenAdd = () => switchToModal(MODALS.ADD);
	const onOpenDetails = member => switchToModal(MODALS.DETAILS, member);
	const onOpenEdit = member => switchToModal(MODALS.EDIT, member);
	const onOpenPasswordReset = member => switchToModal(MODALS.PASSWORD_RESET,
			member);
	const onOpenSuspend = member => switchToModal(MODALS.SUSPEND, member);
	const onOpenActivate = member => switchToModal(MODALS.ACTIVATE, member);

	const onAddDone = member => {
		setActiveMembersList([...activeMembersList, {...member, status: 'Active'}]);
	};

	const onEditDone = member => {
		const mergeMember = m => {
			return m.associateOID === member.associateOID ? {...m, ...member} : m;
		};
		const newActiveList = activeMembersList.map(mergeMember);
		const newSuspendedList = suspendedMembersList.map(mergeMember);
		setActiveMembersList(newActiveList);
		setSuspendedMembersList(newSuspendedList);
		closeModals();
	};

	useEffect(() => {
		const sessionAoidArr = _.get(session, 'sub', '').split('/');
		setAoid(sessionAoidArr[sessionAoidArr.length - 1]);
		dispatch(updateApplicationTitle('Members'));
		fetchMembers();
		fetchRoles();
		fetchAllRoles();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [observerSize, setObserverSize] = useState({
		width: '2000', height: '4000',
	});
	const parentRef = useRef(null);

	useEffect(() => {
		if (!parentRef.current) return; // wait for the parentRef to be available
		const resizeObserver = new ResizeObserver((elem) => {
			setObserverSize({
				width: _.get(elem[0], 'contentRect.width', 2000),
				height: _.get(elem[0], 'contentRect.height', 2000),
			});
		});

		resizeObserver.observe(parentRef.current);
		return () => resizeObserver.disconnect(); // clean up
	}, []);

	useEffect(() => { if (toast[0]) { setToastCount((prev) => prev + 1);}},
			[toast]);

	useEffect(() => {
		if (refreshMembers) {
			try {
				const newMembersList = membersList.map((member) => {
					if (member.associateOID === refreshMembers) {
						if (modeState === 'activate') {
							return {...member, status: 'Active', isSuspended: false};
						} else
							if (modeState === 'suspend') {
								return {...member, status: 'Suspended', isSuspended: true};
							}
					}
					return member;
				});
				setMembersList(orderBy(newMembersList,
						['status', member => member.firstName.toLowerCase()],
						['asc', 'asc']));
				setRefreshMembers('');
				setModeState('');

			} catch (err) {
				setRefreshMembers('');
				setModeState('');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshMembers]);

	const filterRolesList = () => {
		const currentPartnerType = !adminView
				? (roles.includes('selfservicedataclouduser')
						? 'datacloud'
						: 'marketplace')
				: partnerType;

		if (currentPartnerType === 'marketplace') {
			return RolesList.filter(role => role.order !== 4);

		} else {
			return RolesList.filter(role => (role.order !== 1 && role.order !== 3 && role.order !== 5));
		}
	};

	return (<div className={!adminView ? 'members-grid grid' : ''} ref={parentRef}>
    {!adminView && <>
        <div style={{width: '80%', maxWidth: '1400px', margin: '0 auto'}}>
            <div className={'pt-10'}>
                <span className={'mt-8 text-md leading-none pl-1'}>Members/</span>
                <h1 className={'mt-8 mb-12 text-5xl font-black leading-xs tracking-xs'}>
                    Members
                </h1>
                <span className={'block text-3xl font-extrabold leading-xs tracking-xs'}>
                    Manage team access and roles
                </span>
                <div className={'break-words text-lg leading-sm mt-4 mb-4'}>
                    Add team members that will need access to this application and
                    assign them roles so you may work together. You may also manage
                    permissions by resetting password or suspending a team member's
                    access in the event they leave the company.
                </div>
            </div>
            
            <div className="members">
                <div className={`flex flex-row gap-12 justify-center`}
                     style={(size.x <= 1392 || observerSize.width <= 1392)
                             ? {flexWrap: 'wrap'}
                             : {}}>
                    <div>
                        <MemberRolesList
                            list={filterRolesList()}
                            observerSize={observerSize}
                            app="pss"
                        />
                        <SdfAlert status="warning" size="sm" className="mb-1 max-w-72"
                                  aria-live="off">
                            <span slot="title">Suspended users</span>
                            The member(s) in this list marked suspended have either been
                            suspended manually or by the system due to no login activity.
                            Please view the detail of the member for the suspended reason.
                        </SdfAlert>
                    </div>
                    <MembersCard
                        style={{flexBasis: 'min-content'}}
                        orgName={adminView ? orgName : session.orgName}
                        data={membersList}
                        fetch={fetchMembers}
                        loading={appState.loading}
                        error={errorLoadingMembers}
                        openDetails={onOpenDetails}
                        openEdit={onOpenEdit}
                        openPasswordReset={onOpenPasswordReset}
                        openSuspend={onOpenSuspend}
                        openActivate={onOpenActivate}
                        type="active"
                        roleDescriptions={roleDescriptions}
                        partnerType={partnerType}
                        openAdd={onOpenAdd}
                        loggedInAoid={loggedInAoid}
                        adminView={adminView}
                        isLoading={loading}
                    />
                </div>
            </div>
        </div>
    </>}
    
    {adminView && <div className="members">
        <div className="flex flex-col">
            <MembersCard
                style={{flexBasis: 'min-content'}}
                orgName={adminView ? orgName : session.orgName}
                data={membersList}
                fetch={fetchMembers}
                loading={appState.loading}
                error={errorLoadingMembers}
                openDetails={onOpenDetails}
                openEdit={onOpenEdit}
                openPasswordReset={onOpenPasswordReset}
                openSuspend={onOpenSuspend}
                openActivate={onOpenActivate}
                type="active"
                roleDescriptions={roleDescriptions}
                partnerType={partnerType}
                openAdd={onOpenAdd}
                loggedInAoid={loggedInAoid}
                adminView={adminView}
                isLoading={loading}
            />
        </div>
    </div>}

        <MembersAdd
            isOpen={openAddModal}
            onClose={closeModals}
            onDone={onAddDone}
            adminView={adminView}
            ooid={ooid}
            fetchMembers={fetchMembers}
            memberRoles={memberRoles}
            setToastData={setToastData}
            roleDescriptions={roleDescriptions}
            setMembersList={setMembersList}
        />

        <MembersEdit
            member={selectedMember}
            fetchMembers={fetchMembers}
            isOpen={openEditModal}
            onClose={closeModals}
            onDone={onEditDone}
            memberRoles={memberRoles}
            adminView={adminView}
            ooid={ooid}
            partnerType={partnerType}
            allRoles={allRoles}
            setToastData={setToastData}
            roleDescriptions={roleDescriptions}
            setMembersList={setMembersList}
        />

        {!refreshMembers && openPasswordResetModal && <ResetSuspendActivateWrapper
            member={selectedMember}
            isOpen={openPasswordResetModal}
            onClose={closeModals}
            mode="reset"
            setModeState={setModeState}
            roleDescriptions={roleDescriptions}
            setRefreshMembers={setRefreshMembers}
            partnerType={partnerTypeFromRoles}
            adminView={adminView}
            ooid={ooid}
        />}

        {!refreshMembers && openActivateModal && <ResetSuspendActivateWrapper
            member={selectedMember}
            fetchMembers={fetchMembers}
            isOpen={openActivateModal}
            onClose={closeModals}
            mode="activate"
            setModeState={setModeState}
            roleDescriptions={roleDescriptions}
            setRefreshMembers={setRefreshMembers}
            partnerType={partnerTypeFromRoles}
            adminView={adminView}
            ooid={ooid}
        />}

        {!refreshMembers && openSuspendModal && <ResetSuspendActivateWrapper
            member={selectedMember}
            fetchMembers={fetchMembers}
            isOpen={openSuspendModal}
            onClose={closeModals}
            mode="suspend"
            setModeState={setModeState}
            roleDescriptions={roleDescriptions}
            setRefreshMembers={setRefreshMembers}
            partnerType={partnerTypeFromRoles}
            adminView={adminView}
            ooid={ooid}
        />}

        {toast[0] && (<Toast
            key={toast[1]['message'] + toastCount}
            status={toast[1]['status']}
            message={toast[1]['message']}
            showToast={'true'}
        />)}

        {refreshMembers &&
            <SdfBusyIndicator overlay={true} size={'lg'} fullscreen={true}/>}

    </div>);
}

export default withSession(MemberManagement);
